import React, { useEffect, useRef, useState } from 'react';
import { collection, query, where, orderBy, documentId, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { newLocationId } from './locationUtils';
import { toKey, addDays } from './dateUtils';

const clone = (list) => list.map((l) => ({ ...l }));
const emptyLocation = () => ({ id: newLocationId(), name: '', address: '', startTime: '', endTime: '' });

function LocationsEditor({ savedLocations, loaded, onSave, onDirtyChange }) {
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const dirtyRef = useRef(false);

  // Keep the draft in sync with what's saved. If the user is mid-edit, only merge in
  // locations that were added elsewhere (e.g. "Save to my locations" on the schedule tab).
  useEffect(() => {
    if (!loaded) return;
    setDraft((prev) => {
      if (!prev || !dirtyRef.current) return clone(savedLocations);
      const ids = new Set(prev.map((l) => l.id));
      return [...prev, ...clone(savedLocations.filter((l) => !ids.has(l.id)))];
    });
  }, [savedLocations, loaded]);

  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(savedLocations);
  dirtyRef.current = dirty;
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const updateLoc = (i, field, value) => {
    setDraft((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  };

  const addLoc = () => setDraft((prev) => [...prev, emptyLocation()]);

  const removeLoc = (i) => setDraft((prev) => prev.filter((_, idx) => idx !== i));

  const moveLoc = (i, delta) => {
    setDraft((prev) => {
      const j = i + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setStatus('');
    try {
      const clean = await onSave(draft);
      dirtyRef.current = false;
      setDraft(clone(clean));
      setStatus('Saved! These locations are now in the schedule picker.');
    } catch (err) {
      console.error(err);
      setStatus('Save failed — check your connection and try again.');
    }
    setSaving(false);
  };

  const discard = () => {
    dirtyRef.current = false;
    setDraft(clone(savedLocations));
    setStatus('');
  };

  // Pull every location name used on the schedule in the last 6 months that isn't saved yet.
  const importFromSchedule = async () => {
    setImporting(true);
    setStatus('');
    try {
      const since = toKey(addDays(new Date(), -180));
      const q = query(
        collection(db, 'schedule'),
        where(documentId(), '>=', since),
        orderBy(documentId())
      );
      const snap = await getDocs(q);
      const seen = {};
      snap.forEach((d) => {
        const data = d.data() || {};
        const stops = Array.isArray(data.stops) ? data.stops : data.location ? [data] : [];
        stops.forEach((s) => {
          const name = ((s && s.location) || '').trim();
          if (!name) return;
          const key = name.toLowerCase();
          const entry = seen[key] || (seen[key] = { name, addresses: {}, times: {}, count: 0 });
          entry.count += 1;
          const address = (s.address || '').trim();
          if (address) entry.addresses[address] = (entry.addresses[address] || 0) + 1;
          const t = `${(s.startTime || '').trim()}|${(s.endTime || '').trim()}`;
          if (t !== '|') entry.times[t] = (entry.times[t] || 0) + 1;
        });
      });
      const top = (obj) => Object.keys(obj).sort((a, b) => obj[b] - obj[a])[0] || '';
      const existing = new Set(draft.map((l) => l.name.trim().toLowerCase()));
      const additions = Object.values(seen)
        .filter((e) => !existing.has(e.name.toLowerCase()))
        .sort((a, b) => b.count - a.count)
        .map((e) => {
          const [startTime, endTime] = top(e.times).split('|');
          return {
            id: newLocationId(),
            name: e.name,
            address: top(e.addresses),
            startTime: startTime || '',
            endTime: endTime || '',
          };
        });
      if (additions.length === 0) {
        setStatus('Nothing new to add — every location from the last 6 months is already saved.');
      } else {
        setDraft((prev) => [...prev, ...additions]);
        setStatus(`Added ${additions.length} location${additions.length === 1 ? '' : 's'} from your past schedule. Look them over, then tap Save Locations.`);
      }
    } catch (err) {
      console.error(err);
      setStatus('Could not read the schedule — try again.');
    }
    setImporting(false);
  };

  if (!draft) {
    return <div className="schedule-loading"><div className="schedule-spinner" /></div>;
  }

  return (
    <div className="admin-card">
      <h3>SAVED LOCATIONS</h3>
      <p className="admin-help">
        Save the places you set up at so you can just pick them from a list when building the schedule.
        Usual hours are optional — they pre-fill the times when you pick that location.
      </p>

      {draft.length === 0 && (
        <p className="admin-help">No saved locations yet. Add one below or import the ones you&apos;ve already used.</p>
      )}

      {draft.map((loc, i) => (
        <div key={loc.id} className="admin-loc-card">
          <input
            type="text"
            placeholder="Location name (e.g. Sprouts Apple Valley)"
            value={loc.name}
            onChange={(e) => updateLoc(i, 'name', e.target.value)}
          />
          <input
            type="text"
            placeholder="Address"
            value={loc.address}
            onChange={(e) => updateLoc(i, 'address', e.target.value)}
          />
          <div className="admin-time-row">
            <input
              type="text"
              placeholder="Usual start (9:00 AM)"
              value={loc.startTime}
              onChange={(e) => updateLoc(i, 'startTime', e.target.value)}
            />
            <input
              type="text"
              placeholder="Usual end (2:00 PM)"
              value={loc.endTime}
              onChange={(e) => updateLoc(i, 'endTime', e.target.value)}
            />
          </div>
          <div className="admin-item-actions">
            <button type="button" className="admin-icon-btn" onClick={() => moveLoc(i, -1)} disabled={i === 0} aria-label="Move up">▲</button>
            <button type="button" className="admin-icon-btn" onClick={() => moveLoc(i, 1)} disabled={i === draft.length - 1} aria-label="Move down">▼</button>
            <button type="button" className="admin-remove-btn" onClick={() => removeLoc(i)}>✕ Remove</button>
          </div>
        </div>
      ))}

      <button type="button" className="admin-add-stop-btn" onClick={addLoc}>+ Add a location</button>
      <div className="admin-week-actions admin-actions-tight">
        <button type="button" className="admin-link-btn" onClick={importFromSchedule} disabled={importing}>
          {importing ? 'Looking through your schedule...' : 'Import locations from past schedule'}
        </button>
      </div>

      <button className="cta-button admin-full-btn" onClick={save} disabled={saving || !dirty}>
        {saving ? 'Saving...' : dirty ? 'Save Locations' : 'Saved'}
      </button>
      {dirty && (
        <p className="admin-unsaved">
          Unsaved changes · <button type="button" className="admin-link-btn admin-link-sm" onClick={discard}>Undo my changes</button>
        </p>
      )}
      {status && <p className="admin-status">{status}</p>}
    </div>
  );
}

export default LocationsEditor;
